import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import ConfirmationDeleteTableDialog from 'components/ui/feedback/ConfirmationGridDelete';
import React, { useState } from 'react';

interface RowContextMenuProps {
  row: any;
  customActions: any[]; // Accetta sia oggetti che elementi JSX pronti
  shouldRenderDelete: boolean;
  handleOnDelete: (id: any) => void;
  t: any;
}

export const RowContextMenu: React.FC<RowContextMenuProps> = ({
  row,
  customActions,
  shouldRenderDelete,
  handleOnDelete,
  t
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Blocca la selezione della riga
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    setAnchorEl(null);
  };

  // Stato per controllare l'apertura del dialog di conferma eliminazione
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState<number | null>(null);

  const handleOpenDeleteConfirmation = (id: number) => {
    setSelectedDeleteId(id);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteConfirmation = () => {
    setOpenDeleteDialog(false);
    setSelectedDeleteId(null);
  };

  if (!shouldRenderDelete && customActions.length === 0) return null;

  return (
    <>
      <Tooltip title={t('global.actions') || 'Azioni'} arrow placement="top">
        <IconButton size="small" onClick={handleOpen} sx={{ color: 'text.secondary' }}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        id="row-actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => handleClose()}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
            minWidth: 180,
            mt: 0.5
          }
        }}>
        {/* SEZIONE DINAMICA: Renderizza le azioni extra passate alla griglia */}
        {customActions.map((action, index) => {
          // Caso A: L'azione passata è un elemento React/JSX valido (es: <IsTenantAdministrator>...</IsTenantAdministrator>)
          if (React.isValidElement(action)) {
            return (
              <MenuItem
                id="legacy-action-menu-item"
                key={index}
                onClick={() => handleClose()}
                sx={{
                  typography: 'body2',

                  '& button': {
                    width: '100%',
                    justifyContent: 'flex-start !important',
                    textAlign: 'left',
                    textTransform: 'none',
                    // p: '0px !important',
                    background: 'none !important',
                    color: 'text.primary',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'cenbuttonter',
                    gap: '12px'
                  },

                  '& svg': {
                    fontSize: '1.25rem',
                    color: '#334155'
                  }
                }}>
                {action}
              </MenuItem>
            );
          }

          // Caso B: L'azione è passata nel formato oggetto configurabile
          const isActionVisible =
            typeof action.visible === 'function' ? action.visible(row) : action.visible !== false;
          if (!isActionVisible) return null;

          const isDisabled =
            typeof action.disabled === 'function' ? action.disabled(row) : !!action.disabled;

          return (
            <MenuItem
              id="action-menu-item"
              key={action.id || index}
              disabled={isDisabled}
              onClick={(e) => {
                handleClose(e);
                action.onClick(row);
              }}
              sx={{ typography: 'body2', py: 1 }}>
              {action.icon && (
                <ListItemIcon sx={{ minWidth: '30px !important', color: '#334155' }}>
                  {action.icon}
                </ListItemIcon>
              )}
              <ListItemText primary={action.label || action.description || ''} />
            </MenuItem>
          );
        })}

        {/* RETROCOMPATIBILITÀ: Voce di cancellazione fissa sempre in fondo al menu */}
        {shouldRenderDelete && (
          <MenuItem
            id="delete-context-menu"
            onClick={(e) => {
              handleClose(e);
              handleOpenDeleteConfirmation(row['templateId'] || row['id'] || row['_id']);
            }}
            sx={{
              typography: 'body2',
              py: 1,
              borderRadius: 1,
              color: 'error.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: '100%',
              '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.04)' }
            }}>
            <ListItemIcon sx={{ minWidth: '30px !important', color: 'error.main' }}>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('actions.delete') || 'Elimina'} />
          </MenuItem>
        )}
      </Menu>

      {/* Questo dialog risponde SOLO alla voce fissa del menu contestuale */}
      <ConfirmationDeleteTableDialog
        open={openDeleteDialog}
        id={selectedDeleteId}
        onClose={handleCloseDeleteConfirmation}
        onConfirm={handleOnDelete}
      />
    </>
  );
};
